import { __awaiter } from '../_virtual/_tslib.js';
import { useState, useMemo, useEffect } from 'react';
import Authentication from '@lwt-common/authentication/build/Authentication';

const apiDomains = {
  dev: 'https://platform.api.dev.lwolf.com',
  stg: 'https://platform.api.stg.lwolf.com',
  pre: 'https://platform.api.pre.lwolf.com',
  prod: 'https://platform.api.lwolf.com'
};
const useGoogleTagManager = ({
  // GTM-TW6R356J for when you want to publish to the dev environment
  gtmContainerId = 'GTM-TW6R356J',
  applicationCode = '',
  environment = 'dev',
  localStorageTokenKey = 'lwaToken',
  // eslint-disable-next-line max-len
  userToken = ''
}) => {
  var _a, _b, _c, _d, _e;
  const [dataLayerCreated, setDataLayerCreated] = useState(false);
  const [dataLayer, setDataLayer] = useState({
    application: {
      code: applicationCode
    }
  });
  // create a random dataLayer name
  const dataLayerName = useMemo(() => `dataLayer_${Math.random().toString(36).substring(7)}`, []);
  // get the lwaToken from the local storage
  // get the token from cookie if it exists
  const tokenCookie = typeof window !== 'undefined' && document.cookie ? ((_a = document.cookie.split(';').find(item => item.trim().startsWith('lwaToken').valueOf())) === null || _a === void 0 ? void 0 : _a.split('=')[1]) || '' : '';
  const getUserToken = () => {
    // get the token from the local storage or the cookie or the userToken
    return typeof window !== 'undefined' ? window.localStorage.getItem(localStorageTokenKey) || tokenCookie || userToken : '';
  };
  const token = getUserToken();
  const clients = token ? (_c = (_b = Authentication.parseToken(token)) === null || _b === void 0 ? void 0 : _b.getLwtClaim()) === null || _c === void 0 ? void 0 : _c.getClients() : undefined;
  const user = clients ? clients.find(item => item.ctp === 'User') : undefined;
  const userId = (user === null || user === void 0 ? void 0 : user.getUserId()) || ((_e = (_d = Authentication.parseToken(token)) === null || _d === void 0 ? void 0 : _d.getLwtClaim()) === null || _e === void 0 ? void 0 : _e.getUserId());
  const mapData = data => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const clientAddress = ((_a = data === null || data === void 0 ? void 0 : data.client) === null || _a === void 0 ? void 0 : _a.clientAddresses[0]) || {};
    const mappedData = {
      account: {
        id: ((_c = (_b = Authentication.parseToken(token)) === null || _b === void 0 ? void 0 : _b.getLwtClaim()) === null || _c === void 0 ? void 0 : _c.getUserId()) || '',
        name: ((_d = Authentication.parseToken(token)) === null || _d === void 0 ? void 0 : _d.getFirstName()) + ' ' + ((_e = Authentication.parseToken(token)) === null || _e === void 0 ? void 0 : _e.getLastName()),
        emailAddress: ((_f = Authentication.parseToken(token)) === null || _f === void 0 ? void 0 : _f.getEmail()) || ''
      }
    };
    // Conditionally include the office information if it exists
    if (data.client) {
      mappedData.client = {
        id: data.client.id || '',
        name: data.client.name || '',
        city: clientAddress.city || '',
        provinceCode: clientAddress.provinceCode || '',
        postalCode: clientAddress.postalCode || '',
        countryCode: clientAddress.countryCode || ''
      };
    }
    mappedData.user = {
      id: data.id || '',
      clientId: data.clientId || '',
      brokerageId: data.brokerageId || '',
      officeId: data.officeId || '',
      name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
      emailAddress: data.emailAddress || '',
      role: {
        id: ((_g = data === null || data === void 0 ? void 0 : data.role) === null || _g === void 0 ? void 0 : _g.id) || '',
        name: ((_h = data === null || data === void 0 ? void 0 : data.role) === null || _h === void 0 ? void 0 : _h.name) || ''
      },
      products: (_j = data === null || data === void 0 ? void 0 : data.applications) === null || _j === void 0 ? void 0 : _j.map(app => ({
        id: app.id || '',
        name: app.name || ''
      }))
    };
    if (data.office) {
      mappedData.office = {
        id: data.office.id || '',
        clientId: data.office.clientId || '',
        brokerageId: data.office.brokerageId || '',
        name: data.office.name || '',
        city: data.office.city || '',
        provinceCode: data.office.provinceCode || '',
        postalCode: data.office.postalCode || '',
        countryCode: data.office.countryCode || ''
      };
    }
    return mappedData;
  };
  const APIEndPoint = `${apiDomains[environment]}/v1/users/${userId}/summary`;
  const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h, _j, _k;
    if (!userId) {
      console.log('User Id not found, dataLayer must be sent with the setDataLayer function from the parent component');
      window[dataLayerName] = window[dataLayerName] || [];
      if (dataLayer && dataLayer.user && dataLayer.user.id) {
        setDataLayerCreated(true);
      } else {
        return;
      }
      setDataLayer({
        application: Object.assign({}, dataLayer.application),
        account: Object.assign({
          id: ((_g = (_f = Authentication.parseToken(token)) === null || _f === void 0 ? void 0 : _f.getLwtClaim()) === null || _g === void 0 ? void 0 : _g.getUserId()) || '',
          name: ((_h = Authentication.parseToken(token)) === null || _h === void 0 ? void 0 : _h.getFirstName()) + ' ' + ((_j = Authentication.parseToken(token)) === null || _j === void 0 ? void 0 : _j.getLastName()),
          emailAddress: ((_k = Authentication.parseToken(token)) === null || _k === void 0 ? void 0 : _k.getEmail()) || ''
        }, dataLayer.account),
        user: Object.assign({}, dataLayer.user),
        client: Object.assign({}, dataLayer.client),
        office: Object.assign({}, dataLayer.office)
      });
      return;
    }
    if (!token) return console.log('Token not found');
    try {
      const response = yield fetch(APIEndPoint, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = yield response.json();
      window[dataLayerName] = window[dataLayerName] || [];
      setDataLayerCreated(true);
      setDataLayer(Object.assign(Object.assign(Object.assign({}, mapData(data)), dataLayer), {
        application: Object.assign({}, dataLayer.application),
        account: Object.assign(Object.assign({}, mapData(data).account), dataLayer.account),
        user: Object.assign(Object.assign({}, mapData(data).user), dataLayer.user),
        client: Object.assign(Object.assign({}, mapData(data).client), dataLayer.client),
        office: Object.assign(Object.assign({}, mapData(data).office), dataLayer.office)
      }));
    } catch (error) {
      console.error(error);
    }
  });
  useEffect(() => {
    if (dataLayerCreated) return;
    fetchData();
  }, [APIEndPoint, userId, token, dataLayer, dataLayerCreated, dataLayer]);
  useEffect(() => {
    if (!dataLayerCreated) return;
    if (!dataLayer) return;
    if (!dataLayerName) return;
    if (!token) return;
    // remove the old dataLayer push script
    const oldDataLayerPushScript = document.querySelector('script[data-layer-push]');
    if (oldDataLayerPushScript) {
      document.head.removeChild(oldDataLayerPushScript);
    }
    const dataLayerPushScript = document.createElement('script');
    dataLayerPushScript.setAttribute('data-layer-push', '');
    dataLayerPushScript.innerHTML = `window.${dataLayerName}.push(${JSON.stringify(dataLayer)});`;
    // only add if valid dataLayer
    document.head.appendChild(dataLayerPushScript);
  }, [dataLayer, token, dataLayerName, dataLayer]);
  useEffect(() => {
    if (!dataLayerCreated) return;
    if (!token) return;
    if (!dataLayerName) return;
    const tagMangerDataLayerScript = document.createElement('script');
    // create the dataLayer script tag
    tagMangerDataLayerScript.innerHTML = `window.${dataLayerName} = window.${dataLayerName}`;
    // add the dataLayer script tag after the head tag
    document.head.appendChild(tagMangerDataLayerScript);
    // add some sample data to the dataLayer
    const script = document.createElement('script');
    // add a type attribute to the script tag
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','${dataLayerName}','${gtmContainerId}');`;
    // add the GTM script after the head tag
    document.head.appendChild(script);
    // add the GTM noscript after the body tag
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmContainerId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.appendChild(noscript);
  }, [gtmContainerId, dataLayerCreated, token, dataLayerName]);
  return {
    dataLayer,
    setDataLayer
  };
};

export { useGoogleTagManager as default };
//# sourceMappingURL=useGoogleTagManager.js.map
